import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { X } from 'lucide-react';

const StateEditor = ({ 
  state = null, // null for new state
  isInitial = false,
  onSave, 
  onCancel,
  inline = false 
}) => {
  const [name, setName] = useState(state?.id || '');
  const [type, setType] = useState(state?.type || 'atomic');
  const [markAsInitial, setMarkAsInitial] = useState(isInitial);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (state) {
      setName(state.id || '');
      setType(state.type || 'atomic');
    }
  }, [state]);

  const validate = () => {
    const errs = [];
    if (!name.trim()) {
      errs.push('State name is required');
    }
    if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(name)) {
      errs.push('State name must start with letter and contain only alphanumeric characters');
    }
    setErrors(errs);
    return errs.length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    
    onSave({
      id: name,
      type,
      markAsInitial: !state && markAsInitial // Only for new states
    });
  };

  if (inline) {
    return (
      <div 
        className="absolute bg-[#1a1f3a] border-2 border-blue-500 rounded-lg p-3 shadow-xl z-50 w-72"
        data-testid="state-editor-inline"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold">{state ? 'Edit State' : 'Create State'}</h3>
          <button onClick={onCancel} className="text-gray-500 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="StateName"
              className="mt-1 h-8 text-sm"
              autoFocus
              data-testid="state-name-input"
            />
          </div>

          <div>
            <Label className="text-xs">Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="mt-1 h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="atomic">Atomic</SelectItem>
                <SelectItem value="compound">Compound</SelectItem>
                <SelectItem value="final">Final</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {!state && (
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="initial" 
                checked={markAsInitial}
                onCheckedChange={setMarkAsInitial}
              />
              <Label htmlFor="initial" className="text-xs cursor-pointer">
                Mark as initial state
              </Label>
            </div>
          )}

          {errors.length > 0 && (
            <div className="text-xs text-red-400 space-y-1">
              {errors.map((err, idx) => (
                <div key={idx}>• {err}</div>
              ))}
            </div>
          )}

          <div className="flex space-x-2 pt-2">
            <Button 
              onClick={handleSave} 
              size="sm" 
              className="flex-1 h-8 text-xs"
              data-testid="state-save-button"
            >
              {state ? 'Update' : 'Create'}
            </Button>
            <Button 
              onClick={onCancel} 
              size="sm" 
              variant="outline" 
              className="h-8 text-xs"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Bottom sheet for mobile (simplified for now)
  return (
    <div className="fixed inset-x-0 bottom-0 bg-[#1a1f3a] border-t-2 border-blue-500 rounded-t-2xl p-4 z-50 animate-slide-up">
      <div className="max-w-md mx-auto space-y-4">
        <h3 className="text-lg font-bold">{state ? 'Edit State' : 'Create State'}</h3>
        
        <div>
          <Label>Name</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="StateName"
            className="mt-2"
            autoFocus
          />
        </div>

        <div>
          <Label>Type</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="mt-2">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="atomic">Atomic</SelectItem>
              <SelectItem value="compound">Compound</SelectItem>
              <SelectItem value="final">Final</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {!state && (
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="initial-mobile" 
              checked={markAsInitial}
              onCheckedChange={setMarkAsInitial}
            />
            <Label htmlFor="initial-mobile" className="cursor-pointer">
              Mark as initial state
            </Label>
          </div>
        )}

        {errors.length > 0 && (
          <div className="text-sm text-red-400 space-y-1">
            {errors.map((err, idx) => (
              <div key={idx}>• {err}</div>
            ))}
          </div>
        )}

        <div className="flex space-x-3 pt-2">
          <Button onClick={handleSave} className="flex-1">
            {state ? 'Update' : 'Create'}
          </Button>
          <Button onClick={onCancel} variant="outline">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StateEditor;
